// gating.ts — pure campaign unlock/availability logic (no React).
//
// Single source of truth for "is this chapter/activity open, and what blocks it".
// Used by the campaign detail UI and by the home "next session" banner.
//
// A campaign's end date closes its live sessions and nothing else: chapters
// open by their own rules before the end and after it, and every activity that
// needs no session can still be finished, and earns its XP, once the campaign
// has ended. A gameplay activity still waiting on a session then says there are
// no more (noMoreSessions), and neither blocks what follows it nor reads locked.

import { sessionShown } from '../session-attendance';
import type { CampaignStatus } from '../../_shared/types';
import { currentSeconds } from '../../../utils/use-now-seconds';
import { percentOf } from '../../../utils/percent';
import type { StudentCampaignDetail, StudentCampaignChapter, StudentActivity } from './types';

/**
 * Where a campaign or a chapter stands for the student: done, open now, past
 * its live sessions with work that can still be finished (a campaign) or
 * nothing left that can (a chapter), opening later, or shut.
 */
export type ProgressStatus = 'completed' | 'active' | 'ended' | 'upcoming' | 'locked';

/**
 * Where a campaign stands for the student: completed once the student has
 * completed it, and otherwise by its lifecycle — active, ended once its end
 * date has passed (its live sessions are over; everything else can still be
 * finished), upcoming while scheduled, locked while a draft. The home's cards,
 * the campaign list and a campaign's own page read it alike.
 */
export function campaignStatus(campaign: { status: CampaignStatus; completedAt: string | null }): ProgressStatus {
  if (campaign.completedAt) return 'completed';
  if (campaign.status === 'FINISHED') return 'ended';
  if (campaign.status === 'ACTIVE') return 'active';
  if (campaign.status === 'SCHEDULED') return 'upcoming';
  return 'locked';
}

/** Whether a campaign is current for the student — active or upcoming: what the list's first tab holds, and the home shows first. */
export const isCurrentCampaign = (status: ProgressStatus): boolean => status === 'active' || status === 'upcoming';

/** A campaign as the home's list reads one: its lifecycle, whether the student completed it, and its dates. */
interface HomeCampaign {
  status: CampaignStatus;
  completedAt: string | null;
  startDateUtc: number | null;
  endDateUtc: number | null;
}

/** Soonest-starting first, as the campaign list orders every tab; one with no start last. */
const byStart = (a: { startDateUtc: number | null }, b: { startDateUtc: number | null }) =>
  (a.startDateUtc ?? Number.POSITIVE_INFINITY) - (b.startDateUtc ?? Number.POSITIVE_INFINITY);

/**
 * The campaigns the home's "My Campaigns" shows, at most `count`: the current
 * ones, then the ended ones the student has not completed — their work can
 * still be finished, and earns its XP — under their Ended status, each group
 * soonest-starting first as the campaign list orders its tabs. A student whose
 * only campaign has ended still finds it there.
 */
export function homeCampaigns<C extends HomeCampaign>(campaigns: C[], count = 3): C[] {
  const current = campaigns.filter((c) => isCurrentCampaign(campaignStatus(c))).sort(byStart);
  const ended = campaigns.filter((c) => campaignStatus(c) === 'ended').sort(byStart);
  return [...current, ...ended].slice(0, count);
}

/** Why the home has no campaign to show, of the student's: none assigned, every one completed, or none open yet (drafts). */
export type HomeEmpty = 'none' | 'allCompleted' | 'notOpen';

/** What is true of the student's campaigns when the home shows none of them (homeCampaigns). */
export function homeEmpty(campaigns: { status: CampaignStatus; completedAt: string | null }[]): HomeEmpty {
  if (campaigns.length === 0) return 'none';
  return campaigns.every((c) => campaignStatus(c) === 'completed') ? 'allCompleted' : 'notOpen';
}

/** Whether a campaign's live sessions are over: its end date has passed. Nothing else closes with them. */
export const sessionsOver = (campaign: { status: CampaignStatus }): boolean => campaign.status === 'FINISHED';

/** Whether a campaign has begun: its chapters open by their own rules from then on, through its end and after it. */
const campaignStarted = (campaign: { status: CampaignStatus }): boolean =>
  campaign.status === 'ACTIVE' || campaign.status === 'FINISHED';

/**
 * What keeps a chapter shut, outermost first: its campaign not yet begun, no
 * start date of its own, its start date still to come, or the mandatory work
 * of the chapter before it not done.
 */
export type ChapterShut = 'campaignNotStarted' | 'notScheduled' | 'beforeStart' | 'prevChapter';

interface ChapterAvailability {
  chapterOpen: boolean;
  notScheduled: boolean;
  beforeStart: boolean;
  prevChapterLocked: boolean;
  /**
   * Every reason it is shut, outermost first (ChapterShut), for the words that
   * name them (chapter-shut.ts): the first is the one a shut activity's note
   * names, and the campaign's trail names each. Empty while it is open.
   */
  shut: ChapterShut[];
}

/** Fully done = completed AND (for gameplay) participated + reflection submitted. */
export function isActivityFullyDone(a: StudentActivity): boolean {
  if (!a.completion) return false;
  const rj = a.completion.resultJson as Record<string, unknown> | null | undefined;
  if (a.type === 'GAMEPLAY') {
    if (rj?.participated === false) return false; // missed session — not "fully done"
    if (rj?.participated === true && !rj?.reflection) return false; // reflection pending
  }
  return true;
}

/** A gameplay activity whose session the student did not join. */
export function isMissedSession(a: StudentActivity): boolean {
  const rj = a.completion?.resultJson as Record<string, unknown> | null | undefined;
  return a.type === 'GAMEPLAY' && sessionShown(a.worldSession?.status ?? 'FINISHED', rj?.participated) === 'missed';
}

/**
 * A gameplay activity still waiting on a session that will not come: its
 * campaign's sessions are over, it has no completion, and no session of its is
 * live now. One the student played, or missed, says so instead.
 */
export function noMoreSessions(a: StudentActivity, campaign: { status: CampaignStatus }): boolean {
  return a.type === 'GAMEPLAY' && sessionsOver(campaign) && !a.completion && a.worldSession?.status !== 'RUNNING';
}

/** True if this activity counts toward unblocking the next mandatory step.
 *  A missed gameplay session still unblocks the chain (player won't be stuck forever). */
export function isMandatoryUnlockDone(a: StudentActivity): boolean {
  if (!a.completion) return false;
  if (isMissedSession(a)) return true; // missed → still unblocks
  return isActivityFullyDone(a);
}

/**
 * Whether an activity lets the mandatory ones after it open: done, missed, or
 * — once its campaign's sessions are over — a gameplay activity no session can
 * complete any more, which would otherwise hold the rest shut for good.
 */
function passesForUnlock(a: StudentActivity, campaign: { status: CampaignStatus }): boolean {
  return isMandatoryUnlockDone(a) || noMoreSessions(a, campaign);
}

/** True if all mandatory activities in the previous chapter let the next ones open (passesForUnlock). */
export function prevChapterMandatoryDone(campaign: StudentCampaignDetail, chapter: StudentCampaignChapter): boolean {
  const sorted = [...campaign.chapters].sort((a, b) => a.orderIndex - b.orderIndex);
  const idx = sorted.findIndex((c) => c.id === chapter.id);
  if (idx <= 0) return true;
  const prev = sorted[idx - 1];
  return prev.activities.filter((a) => a.category === 'MANDATORY').every((a) => passesForUnlock(a, campaign));
}

/**
 * Whether a chapter is open, and what keeps it shut: its own start date, the
 * mandatory work of the chapter before it, and the campaign's having begun.
 * The campaign's end date is not among them.
 */
export function chapterAvailability(ch: StudentCampaignChapter, campaign: StudentCampaignDetail): ChapterAvailability {
  const nowSec = currentSeconds();
  const started = campaignStarted(campaign);
  const notScheduled = ch.startDateUtc === null;
  const beforeStart = ch.startDateUtc !== null && nowSec < ch.startDateUtc;
  const prevChapterLocked = !prevChapterMandatoryDone(campaign, ch);
  const chapterOpen = started && !notScheduled && !beforeStart && !prevChapterLocked;
  const reasons: [boolean, ChapterShut][] = [
    [!started, 'campaignNotStarted'],
    [notScheduled, 'notScheduled'],
    [beforeStart, 'beforeStart'],
    [prevChapterLocked, 'prevChapter'],
  ];
  const shut = reasons.filter(([holds]) => holds).map(([, why]) => why);
  return { chapterOpen, notScheduled, beforeStart, prevChapterLocked, shut };
}

/** True when every MANDATORY activity in the chapter lets the next ones open
 *  (a missed gameplay counts, and one no session can complete any more). Used
 *  to decide when the "current chapter" advances — optional activities left
 *  over don't keep a chapter as current. */
export function chapterMandatoryComplete(ch: StudentCampaignChapter, campaign: StudentCampaignDetail): boolean {
  return ch.activities.filter((a) => a.category === 'MANDATORY').every((a) => passesForUnlock(a, campaign));
}

/** Whether the student can still do something about an activity: not done, not a missed session, and not waiting on a session that will not come. */
const stillToDo = (a: StudentActivity, campaign: StudentCampaignDetail): boolean =>
  !isActivityFullyDone(a) && !isMissedSession(a) && !noMoreSessions(a, campaign);

/**
 * Where a chapter stands: completed once every activity is done; open with
 * work to do (active) — or, its campaign's sessions over, with nothing left
 * that can still be done (ended); upcoming before its start date or without
 * one; locked behind the chapter before it.
 */
export function chapterStatus(ch: StudentCampaignChapter, campaign: StudentCampaignDetail): ProgressStatus {
  const av = chapterAvailability(ch, campaign);
  const total = ch.activities.length;
  const completed = ch.activities.filter(isActivityFullyDone).length;
  if (total > 0 && completed === total) return 'completed';
  if (av.chapterOpen) {
    return sessionsOver(campaign) && !ch.activities.some((a) => stillToDo(a, campaign)) ? 'ended' : 'active';
  }
  if (av.beforeStart || av.notScheduled) return 'upcoming';
  return 'locked';
}

/**
 * A chapter's progress by both its measures, as every place that shows it says
 * them: its activities done of its total, and the share of its XP earned. The
 * overview counted activities where the chapter row and hero measured XP, so
 * one chapter read 17% on one screen and 8% on the next.
 */
export function chapterProgress(ch: StudentCampaignChapter): { done: number; total: number; xpEarned: number; xpTotal: number; xpPct: number } {
  const total = ch.activities.length;
  const done = ch.activities.filter(isActivityFullyDone).length;
  const xpTotal = ch.activities.reduce((s, a) => s + a.xpReward, 0);
  const xpEarned = ch.activities.reduce((s, a) => s + (a.completion?.xpAwarded ?? 0), 0);
  return { done, total, xpEarned, xpTotal, xpPct: percentOf(xpEarned, xpTotal) };
}

/** A campaign's XP earned of its total, and the share, summed from its chapters (chapterProgress), as its hero says them. */
export function campaignXp(campaign: StudentCampaignDetail): { xpEarned: number; xpTotal: number; xpPct: number } {
  const chapters = campaign.chapters.map(chapterProgress);
  const xpEarned = chapters.reduce((s, c) => s + c.xpEarned, 0);
  const xpTotal = chapters.reduce((s, c) => s + c.xpTotal, 0);
  return { xpEarned, xpTotal, xpPct: percentOf(xpEarned, xpTotal) };
}

/** True if the mandatory activity at index `idx` (within the chapter's mandatory
 *  sequence) is available to open: chapter open AND every preceding mandatory
 *  activity lets it open (passesForUnlock). */
export function mandatoryUnlocked(chapter: StudentCampaignChapter, idx: number, campaign: StudentCampaignDetail): boolean {
  if (!chapterAvailability(chapter, campaign).chapterOpen) return false;
  if (idx <= 0) return true;
  const mandatory = chapter.activities.filter((a) => a.category === 'MANDATORY');
  return mandatory.slice(0, idx).every((a) => passesForUnlock(a, campaign));
}

/** Where an activity stands on its chapter's trail. */
export type ActivityState = 'done' | 'missed' | 'noSessions' | 'locked' | 'current';

/**
 * Where an activity stands on its chapter's trail: done once the student has
 * finished it, whatever else holds, so their own work never reads locked;
 * missed for a session they did not join; no more sessions for a gameplay
 * activity its campaign's ended sessions can no longer complete; locked while
 * `open` is false (its chapter shut, or a mandatory one before it not done);
 * and otherwise the one to do.
 */
export function activityState(a: StudentActivity, open: boolean, campaign: { status: CampaignStatus }): ActivityState {
  if (isActivityFullyDone(a)) return 'done';
  if (isMissedSession(a)) return 'missed';
  if (noMoreSessions(a, campaign)) return 'noSessions';
  return open ? 'current' : 'locked';
}

/**
 * Whether the student's session list holds a running session that none of the
 * campaigns given holds: the one case the home's Up Next reads the ended
 * campaigns for, as a session running across its campaign's end is still
 * joinable (pickNextGameplay).
 */
export function runsOutside(campaigns: StudentCampaignDetail[], sessions: { id: string; status: string }[]): boolean {
  const held = new Set(campaigns.flatMap((c) => c.chapters.flatMap((ch) => ch.activities.flatMap((a) => (a.worldSession ? [a.worldSession.id] : [])))));
  return sessions.some((s) => s.status === 'RUNNING' && !held.has(s.id));
}

/** A gameplay session the home's "Up Next" can name: its campaign, chapter and activity, when it starts, and whether it runs now. */
interface GameplayPick {
  campaign: StudentCampaignDetail;
  chapter: StudentCampaignChapter;
  activity: StudentActivity;
  startTimeUtc: number;
  running: boolean;
}

/**
 * The next gameplay session across the campaigns given, for the home's "Up
 * Next": the earliest scheduled or running in a campaign that has not ended,
 * and one still running in a campaign that has — no session starts after a
 * campaign's end, but one already running across it finishes, and stays
 * joinable. A running one starts first, as it began first.
 */
export function pickNextGameplay(campaigns: StudentCampaignDetail[]): GameplayPick | null {
  const picks: GameplayPick[] = [];
  for (const campaign of campaigns) {
    const over = sessionsOver(campaign);
    for (const chapter of campaign.chapters) {
      for (const activity of chapter.activities) {
        const s = activity.worldSession;
        if (activity.type !== 'GAMEPLAY' || !s || s.startTimeUtc == null) continue;
        const running = s.status === 'RUNNING';
        if (!running && (over || s.status !== 'SCHEDULED')) continue;
        picks.push({ campaign, chapter, activity, startTimeUtc: s.startTimeUtc, running });
      }
    }
  }
  picks.sort((a, b) => a.startTimeUtc - b.startTimeUtc);
  return picks[0] ?? null;
}

export type NextGameplayState = 'open' | 'pending' | 'date-locked';

/**
 * Determine why a gameplay activity is (not) reachable, for the home banner:
 * - 'open'        → openable now (click → the activity)
 * - 'pending'     → blocked by incomplete required activities (click → `targetChapterId`)
 * - 'date-locked' → prerequisites done, but the chapter hasn't opened yet by date
 *                   (click → the campaign's chapter list)
 * `pendingCount` = number of incomplete MANDATORY activities before the gameplay.
 */
export function nextGameplayState(
  campaign: StudentCampaignDetail,
  chapter: StudentCampaignChapter,
  gameplay: StudentActivity,
): { state: NextGameplayState; pendingCount: number; targetChapterId: string } {
  const sorted = [...campaign.chapters].sort((a, b) => a.orderIndex - b.orderIndex);
  const passes = (a: StudentActivity) => passesForUnlock(a, campaign);

  // Is the gameplay activity openable right now?
  const av = chapterAvailability(chapter, campaign);
  const mandatory = chapter.activities.filter((a) => a.category === 'MANDATORY');
  const gpIdx = mandatory.findIndex((a) => a.id === gameplay.id);
  const unlockedInChapter = gameplay.category !== 'MANDATORY' || gpIdx < 0 || mandatory.slice(0, gpIdx).every(passes);
  if (av.chapterOpen && unlockedInChapter) {
    return { state: 'open', pendingCount: 0, targetChapterId: chapter.id };
  }

  // Count incomplete mandatory activities standing before the gameplay (campaign order).
  let pendingCount = 0;
  for (const ch of sorted) {
    if (ch.orderIndex > chapter.orderIndex) break;
    for (const a of ch.activities) {
      if (ch.id === chapter.id && a.id === gameplay.id) break; // stop at the gameplay itself
      if (a.category === 'MANDATORY' && !passes(a)) pendingCount++;
    }
    if (ch.id === chapter.id) break;
  }

  // Find the first OPEN chapter (campaign order) that still has an incomplete
  // mandatory activity — that's where the student should go next.
  for (const ch of sorted) {
    if (ch.orderIndex > chapter.orderIndex) break;
    if (!chapterAvailability(ch, campaign).chapterOpen) continue;
    const hasIncomplete = ch.activities.some(
      (a) => a.category === 'MANDATORY' && !(ch.id === chapter.id && a.id === gameplay.id) && !passes(a),
    );
    if (hasIncomplete) return { state: 'pending', pendingCount, targetChapterId: ch.id };
  }

  // Nothing actionable right now → the chapter is simply not open yet (date).
  return { state: 'date-locked', pendingCount: 0, targetChapterId: chapter.id };
}
